import React, {useCallback, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import EditorLayout from '../../../../components/Layouts/EditorLayout';
import {SystemSettingsGetSchema} from '../../../../api/DBSchema/SystemSettings';
import {SMCCheckbox} from '../../../../components/Simple/SMCCheckbox';
import RowLayout from '../../../../components/Layouts/RowLayout';
import SMCLabel from '../../../../components/Simple/SMCLabel';
import {SMCSelect, SMCOption} from '../../../../components/Simple/SMCSelect';
import Indentation from '../../../../components/Simple/Indentation';
import {SMCRadioGroup, SMCRadio} from '../../../../components/Simple/SMCRadio';
import SMCTextInput from '../../../../components/Simple/SMCTextInput';
import EmptyRow from '../../../../components/Simple/EmptyRow';
import Sepperator from '../../../../components/Simple/Sepperator';
import SMCPasswordInput from '../../../../components/Simple/SMCPasswordInput';

const pageSchema = SystemSettingsGetSchema.pages.systemBackup;
const pageId = pageSchema.pageId;

function SystemSettingsSystemBackup({
    systemSettingsObject,
    setSystemSettingsData
}) {
    const { t } = useTranslation();

    const pageData = systemSettingsObject[pageId];
    const setPageValue = useCallback((paramName, value) => {
        setSystemSettingsData([pageId, paramName], value);
    }, [setSystemSettingsData]);

    const isPeriodicBackupSet = pageData[pageSchema.isPeriodicBackupSet];
    const summaryReportsUnits = pageData[pageSchema.summaryReportsUnits];

    // Day number is for weekly and monthly, weekly: sunday-saturday == 1-7, monthly: 1-31 (TODO: get a backend fix for this)
    const dayNumber           = pageData[pageSchema.dayNumber];
    
    // Backup file storage
    const storageMethod       = pageData[pageSchema.storageMethod];
    const networkPath         = pageData[pageSchema.networkPath];
    const ftpServer           = pageData[pageSchema.ftpServer];
    const ftpPort             = pageData[pageSchema.ftpPort];
    const ftpPath             = pageData[pageSchema.ftpPath];
    const ftpUsername         = pageData[pageSchema.ftpUsername];
    const ftpPassword         = pageData[pageSchema.ftpPassword];

    // File name
    const isUseStaticFileName     = pageData[pageSchema.isUseStaticFileName];
    const fileName                = pageData[pageSchema.fileName]; // File name is for static and automatic name
    const isOverwriteFileIfExists = pageData[pageSchema.isOverwriteFileIfExists];
    
    // Values
    const summaryReportsUnitsValues = pageSchema.summaryReportsUnitsValues;
    const storageMethodValues       = pageSchema.storageMethodValues;

    // Fix day number
    useEffect(() => {
        if (
            (summaryReportsUnits === summaryReportsUnitsValues.weekly  && (dayNumber > 7 || dayNumber < 0)) ||
            (summaryReportsUnits === summaryReportsUnitsValues.monthly && (dayNumber > 31 || dayNumber < 0))
        ) {
            setPageValue(pageSchema.dayNumber, 7);
        }
    }, [dayNumber, setPageValue, summaryReportsUnits, summaryReportsUnitsValues]);

    return (
        <EditorLayout>
            <RowLayout spacing="4px">
                <SMCCheckbox
                    checked={isPeriodicBackupSet}
                    onChange={e => setPageValue(pageSchema.isPeriodicBackupSet, e.target.checked)}
                    inline
                />
                <SMCLabel inline>{t('SMP.SETTINGS.SYSTEM_BACKUP.PERFORM')}</SMCLabel>
                <SMCSelect
                    disabled={!isPeriodicBackupSet}
                    value={summaryReportsUnits}
                    onChange={e => setPageValue(pageSchema.summaryReportsUnits, e.target.value)}
                    inline
                >
                    <SMCOption value={summaryReportsUnitsValues.daily}>{t('SMP.DAILY')}</SMCOption>
                    <SMCOption value={summaryReportsUnitsValues.weekly}>{t('SMP.WEEKLY')}</SMCOption>
                    <SMCOption value={summaryReportsUnitsValues.monthly}>{t('SMP.MONTHLY')}</SMCOption>
                </SMCSelect>
                <SMCLabel inline>{t('SMP.SETTINGS.SYSTEM_BACKUP.PERIODIC_BACKUPS')}</SMCLabel>
            </RowLayout>
            {
                isPeriodicBackupSet && summaryReportsUnits === summaryReportsUnitsValues.weekly && (
                    <Indentation indentation="25px">
                        <RowLayout spacing="4px">
                            <SMCLabel inline>{t('SMC_SETTINGS_REPORT_SCHD_EVERY')}</SMCLabel>
                            <SMCSelect
                                value={dayNumber}
                                onChange={e => setPageValue(pageSchema.dayNumber, parseInt(e.target.value))}
                            >
                                <SMCOption value={1}>{t('SMC_SETTINGS_REPORT_SCHD_DAY1')}</SMCOption>
                                <SMCOption value={2}>{t('SMC_SETTINGS_REPORT_SCHD_DAY2')}</SMCOption>
                                <SMCOption value={3}>{t('SMC_SETTINGS_REPORT_SCHD_DAY3')}</SMCOption>
                                <SMCOption value={4}>{t('SMC_SETTINGS_REPORT_SCHD_DAY4')}</SMCOption>
                                <SMCOption value={5}>{t('SMC_SETTINGS_REPORT_SCHD_DAY5')}</SMCOption>
                                <SMCOption value={6}>{t('SMC_SETTINGS_REPORT_SCHD_DAY6')}</SMCOption>
                                <SMCOption value={7}>{t('SMC_SETTINGS_REPORT_SCHD_DAY7')}</SMCOption>
                            </SMCSelect>
                        </RowLayout>
                    </Indentation>
                )
            }
            {
                isPeriodicBackupSet && summaryReportsUnits === summaryReportsUnitsValues.monthly && (
                    <Indentation indentation="25px">
                        <RowLayout spacing="4px">
                            <SMCLabel inline>{t('SMC_SETTINGS_REPORT_SCHD_ONDAY1')}</SMCLabel>
                            <SMCSelect
                                value={dayNumber}
                                onChange={e => setPageValue(pageSchema.dayNumber, parseInt(e.target.value))}
                                inline
                            >
                                {
                                    [...Array(31)].map((e,i) => (                                        
                                        <SMCOption key={i+1} value={i+1}>{i+1}</SMCOption>
                                    ))
                                }
                            </SMCSelect>
                            <SMCLabel inline>{t('SMC_SETTINGS_REPORT_SCHD_ONDAY2')}</SMCLabel>
                        </RowLayout>
                    </Indentation>
                )
            }
            {
                (!isPeriodicBackupSet || summaryReportsUnits === summaryReportsUnitsValues.daily) && (
                    <EmptyRow />
                )
            }
            <SMCLabel bold>{t('SMP.SETTINGS.SYSTEM_BACKUP.FILE_STORAGE')}</SMCLabel>
            <SMCRadioGroup value={storageMethod} onChange={e => setPageValue(pageSchema.storageMethod, e.target.value)}>
                <SMCRadio value={storageMethodValues.ftpUpload} label={t('SMP.SETTINGS.SYSTEM_BACKUP.LINE1')} />
                {
                    storageMethod === storageMethodValues.ftpUpload && (
                        <Indentation>
                            <RowLayout>
                                <SMCTextInput inline value={ftpServer} onChange={e => setPageValue(pageSchema.ftpServer, e.target.value)} label={t('SMP.SETTINGS.SYSTEM_BACKUP.FTP_SERVER')} />
                                <SMCLabel inline style={{ paddingRight: '5px' }}>:</SMCLabel>
                                <SMCTextInput inline value={ftpPort} onChange={e => setPageValue(pageSchema.ftpPort, e.target.value)} width={100} />
                            </RowLayout>
                            <SMCTextInput value={ftpPath} onChange={e => setPageValue(pageSchema.ftpPath, e.target.value)} label={t('SMP.SETTINGS.SYSTEM_BACKUP.PATH_ON_SERVER')} />
                            <SMCTextInput value={ftpUsername} onChange={e => setPageValue(pageSchema.ftpUsername, e.target.value)} label={t('SMP.SETTINGS.SYSTEM_BACKUP.USER_NAME')} />
                            <SMCPasswordInput allowPasswordPeek={true} value={ftpPassword} onChange={e => setPageValue(pageSchema.ftpPassword, e.target.value)} label={t('SMP.SETTINGS.SYSTEM_BACKUP.PASSWORD')} />
                        </Indentation>
                    )
                }
                <SMCRadio value={storageMethodValues.networkPath} label={t('SMP.SETTINGS.SYSTEM_BACKUP.SAVE_THE_FILE')} />
                {
                    storageMethod === storageMethodValues.networkPath && (
                        <Indentation>
                            <SMCTextInput value={networkPath} onChange={e => setPageValue(pageSchema.networkPath, e.target.value)} label={t('SMP.SETTINGS.SYSTEM_BACKUP.LINE2')+':'} />
                        </Indentation>
                    )
                }
            </SMCRadioGroup>
            <Sepperator />
            <SMCLabel bold>{t('SMP.SETTINGS.SYSTEM_BACKUP.FILE_NAME_HEADING')}</SMCLabel>
            <SMCRadioGroup value={isUseStaticFileName ? 'STATIC' : 'AUTO'} onChange={e => setPageValue(pageSchema.isUseStaticFileName, e.target.value === 'STATIC')}>
                <SMCRadio value={'AUTO'} label={t('SMP.SETTINGS.SYSTEM_BACKUP.LINE3')} />
                {
                    !isUseStaticFileName && (
                        <Indentation>
                            <SMCTextInput value={fileName} onChange={e => setPageValue(pageSchema.fileName, e.target.value)} label={t('SMP.SETTINGS.SYSTEM_BACKUP.FILE_NAME_PREFIX')} />
                        </Indentation>
                    )
                }
                <SMCRadio value={'STATIC'} label={t('SMP.SETTINGS.SYSTEM_BACKUP.LINE4')} />
                {
                    isUseStaticFileName && (
                        <Indentation>
                            <SMCTextInput value={fileName} onChange={e => setPageValue(pageSchema.fileName, e.target.value)} label={t('SMP.SETTINGS.SYSTEM_BACKUP.FILE_NAME')} />
                            <SMCCheckbox checked={isOverwriteFileIfExists} onChange={e => setPageValue(pageSchema.isOverwriteFileIfExists, e.target.checked)} label={t('SMP.SETTINGS.SYSTEM_BACKUP.LINE5')} />
                        </Indentation>
                    )
                }
            </SMCRadioGroup>
        </EditorLayout>
    )
}

export default SystemSettingsSystemBackup;